import { useLazyQuery, useMutation } from '@apollo/client';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from 'react-modal';

import { ReactComponent as CloseIcon } from '../../icons/close-square.svg';
import { ADD_STOP_TO_ROUTE } from '../../services/graphql/routes/routes-mutations';
import { GET_AVAILABLE_CLIENTS_FOR_ROUTE } from '../../services/graphql/routes/routes-querys';
import FormButton from '../atoms/FormButton';

interface AvailableClientItem {
  serviceId: string;
  clientId: string;
  clientName: string;
  clientAddress?: string | null;
  containerSize?: number | null;
  pricePerGallon?: number | null;
}

interface AddStopModalProps {
  routeId: string;
  onClose: () => void;
  onStopAdded: () => void;
}

function AddStopModal({ routeId, onClose, onStopAdded }: AddStopModalProps) {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [selectedServiceId, setSelectedServiceId] = useState<string | null>(
    null,
  );
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const [fetchClients, { data, loading }] = useLazyQuery(
    GET_AVAILABLE_CLIENTS_FOR_ROUTE,
  );
  const [addStop, { loading: addLoading }] = useMutation(ADD_STOP_TO_ROUTE);

  const clients: AvailableClientItem[] =
    data?.getAvailableClientsForRoute?.data ?? [];

  useEffect(() => {
    fetchClients({ variables: { routeId } });
  }, [fetchClients, routeId]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      fetchClients({
        variables: {
          routeId,
          search: searchTerm || undefined,
        },
      });
    }, 300);

    return () => clearTimeout(timeout);
  }, [fetchClients, routeId, searchTerm]);

  const closeModal = () => {
    setErrorMsg(null);
    setSelectedServiceId(null);
    onClose();
  };

  const handleAdd = async () => {
    if (!selectedServiceId) return;

    setErrorMsg(null);

    try {
      const result = await addStop({
        variables: {
          routeId,
          serviceId: selectedServiceId,
        },
      });

      if (result.data?.addStopToRoute?.status === 'success') {
        await Promise.resolve(onStopAdded());
        onClose();
        return;
      }
    } catch {
      setErrorMsg(t('dashboard.addStopError'));
      return;
    }

    setErrorMsg(t('dashboard.addStopError'));
  };

  return (
    <Modal
      isOpen={true}
      onRequestClose={closeModal}
      appElement={document.getElementById('root') || undefined}
      style={{
        overlay: {
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 9999,
        },
        content: {
          position: 'absolute',
          top: '50%',
          left: '50%',
          right: 'auto',
          bottom: 'auto',
          transform: 'translate(-50%, -50%)',
          width: '90%',
          maxWidth: '470px',
          borderRadius: '20px',
          padding: '16px',
        },
      }}
      contentLabel="Add Stop"
    >
      <div className="flex h-full flex-col justify-between gap-y-6 p-2">
        <div className="flex flex-row justify-between">
          <div className="flex flex-row gap-x-2">
            <span>{t('dashboard.addStopTitle')}</span>
          </div>
          <div className="flex justify-end ">
            <button onClick={closeModal} type="button">
              <CloseIcon />
            </button>
          </div>
        </div>

        <div className="flex flex-col gap-y-4">
          <div className="flex h-auto w-full flex-col items-start justify-center gap-y-2 self-stretch rounded">
            <div className="w-full">
              <input
                className="w-full border-b-2 border-ReyesGreyLight bg-transparent text-sm text-ReyesGrey focus:outline-none"
                placeholder={t('dashboard.addStopSearch')}
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                type="text"
              />
            </div>
          </div>

          <ul className="flex max-h-[300px] flex-col gap-2 overflow-y-auto">
            {!loading && clients.length === 0 && (
              <li className="text-xs text-gray-400">
                {t('dashboard.addStopEmpty')}
              </li>
            )}

            {clients.map((client) => {
              const isSelected = selectedServiceId === client.serviceId;

              return (
                <li key={client.serviceId}>
                  <button
                    type="button"
                    className={`flex w-full items-start gap-2 rounded-lg border px-4 py-3 text-left shadow-sm ${
                      isSelected
                        ? 'border-ReyesGreen bg-green-50'
                        : 'border-gray-200 bg-white'
                    }`}
                    onClick={() => setSelectedServiceId(client.serviceId)}
                  >
                    <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-gray-100 text-xs font-semibold text-gray-600">
                      +
                    </span>
                    <div className="flex flex-col gap-0.5">
                      <span className="text-sm font-semibold text-ReyesBlack">
                        {client.clientName}
                      </span>
                      <span className="text-xs text-gray-500">
                        {client.clientAddress}
                      </span>
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>

          {errorMsg && <p className="text-sm text-red-600">{errorMsg}</p>}
        </div>

        <div className="mb-2 flex flex-row justify-center gap-x-3">
          <FormButton
            id="cancelAddStop"
            icon={''}
            label="Cancel"
            color="white"
            textColor="text-ReyesGreen"
            type="button"
            reverse
            width="auto"
            onClick={closeModal}
          />
          <FormButton
            icon={''}
            type="button"
            id="confirmAddStop"
            label={t('dashboard.addStopConfirm')}
            color="ReyesGreen"
            reverse
            width="auto"
            onClick={handleAdd}
            disabled={!selectedServiceId || addLoading}
          />
        </div>
      </div>
    </Modal>
  );
}

export default AddStopModal;
